import {
  DraftCustomAudience,
  DraftCustomAudienceCriterionOption,
  FieldworkAudience,
  FieldworkAudienceType
} from '../../../data/model/audience'
import { FieldKind } from '../hooks/useSaveTranslation'

export interface AudienceTranslatableField {
  kind: FieldKind
  // For audienceQuestionText: the FieldworkAudience.id (memberId).
  // For audienceOptionValue: question.questionId.
  saveId: string
  // Sub id for options — responseOptionId.
  subId?: string
  // Position-based key — e.g. "A1.text", "A1.option.2". 1-based index across
  // the filtered custom-audience-only member list (matches the "A1", "A2"
  // labels rendered under "Custom screeners" in the Translations route).
  key: string
  label: string
  master: string
  getCurrent: (languageCode: string) => string
  // Whether this field counts toward launch-gate completion. Audience fields
  // always gate launch — custom screener text is respondent-facing and must be
  // translated before launch.
  countsTowardCompletion: boolean
}

const findQuestionTranslation = (
  member: DraftCustomAudience,
  languageCode: string
): string => {
  const translation = member.question.translations?.find(
    (t) => t.language === languageCode
  )
  return translation?.text ?? ''
}

const findOptionTranslation = (
  option: DraftCustomAudienceCriterionOption,
  languageCode: string
): string => {
  const translation = option.responseOption.translations?.find(
    (t) => t.language === languageCode
  )
  return translation?.value ?? ''
}

export interface CustomAudienceTranslationGroup {
  memberId: string
  // 1-based index within the filtered custom-audience list. Used as the
  // "A<position>" prefix in field keys and the card's positionLabel.
  position: number
  fields: AudienceTranslatableField[]
}

/**
 * `globalOnly` (multi-market): keep only isGlobal members. A market-local
 * screener is authored in its own market's language with English stored as the
 * *translation*, so it has nothing to translate outward — and its own language
 * can never be satisfied, which made those markets unlaunchable.
 */
export const extractCustomAudienceMembers = (
  members: ReadonlyArray<FieldworkAudience>,
  { globalOnly = false }: { globalOnly?: boolean } = {}
): CustomAudienceTranslationGroup[] => {
  return members
    .filter((m) => m.memberType === FieldworkAudienceType.Custom)
    .filter((m) => !globalOnly || !!m.isGlobal)
    .map((m, idx) => {
      const position = idx + 1
      const a = `A${position}`
      const item = m.memberItem as DraftCustomAudience
      const memberId = m.id
      const fields: AudienceTranslatableField[] = [
        {
          kind: 'audienceQuestionText',
          saveId: memberId,
          key: `${a}.text`,
          label: 'Question text',
          master: item.question.text,
          getCurrent: (lang) => findQuestionTranslation(item, lang),
          countsTowardCompletion: true
        }
      ]
      item.criterionOptions.forEach((opt, optIdx) => {
        const optId = opt.responseOption.responseOptionId
        fields.push({
          kind: 'audienceOptionValue',
          saveId: item.question.questionId,
          subId: optId,
          key: `${a}.option.${optIdx + 1}`,
          label: `Option ${optIdx + 1}`,
          master: opt.responseOption.value,
          getCurrent: (lang) => findOptionTranslation(opt, lang),
          countsTowardCompletion: true
        })
      })
      return { memberId, position, fields }
    })
}
